import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Server, Zap } from 'lucide-react'

const pillar1Features = [
  '24/7 monitoring & proactive issue resolution',
  'Unlimited support — no hourly billing surprises',
  'Cloud migration, email setup, device management',
  'Anti-virus, automated backups, security patching',
  'Hardware procurement at cost',
  'Strategic technology roadmapping',
]

const pillar2Features = [
  'Workflow automation (intake, scheduling, reporting, compliance docs)',
  'AI tool implementation with role-based access controls',
  'Process mapping & efficiency audits',
  'Custom AI integrations for your existing stack',
  "ROI tracking — you'll know exactly what you saved",
  'Ongoing optimization as AI evolves',
]

export function TwoPillars() {
  return (
    <section style={{ padding: '120px 0', background: 'var(--bg)' }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
          style={{ marginBottom: 72 }}
        >
          <h2
            style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(42px, 5.5vw, 68px)',
              textTransform: 'uppercase',
              lineHeight: 1.0,
              color: 'var(--white)',
            }}
          >
            Two Pillars. <span style={{ color: 'var(--accent)' }}>One Partner.</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: 'var(--white-dim)',
              marginTop: 20,
              maxWidth: 520,
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.65,
            }}
          >
            We handle everything your business needs to run, protect, and grow — managed IT that never sleeps, and AI automation that compounds your team's output.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[3px]">
          <motion.div
            initial={false}
            animate={{ opacity: 1, x: 0 }}
            className="pillar-card relative overflow-hidden transition-colors duration-250"
            style={{
              background: 'var(--bg-3)',
              padding: '60px 52px',
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                background: 'var(--accent-glow)',
                border: '1px solid var(--accent-line)',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 28,
                fontSize: 22,
              }}
            >
              <Server className="w-6 h-6" style={{ color: 'var(--accent)' }} />
            </div>
            <h3
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 22,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                marginBottom: 16,
                lineHeight: 1.05,
                color: 'var(--white)',
              }}
            >
              Managed IT & <span style={{ color: 'var(--accent)' }}>Support</span>
            </h3>
            <p
              style={{
                fontSize: 16,
                lineHeight: 1.7,
                color: 'var(--white-dim)',
                marginBottom: 36,
              }}
            >
              Your outsourced IT department. We monitor, protect, and maintain your entire tech environment so your team never has to think about it.
            </p>
            <ul className="space-y-3">
              {pillar1Features.map((f) => (
                <li
                  key={f}
                  style={{
                    fontSize: 14,
                    color: 'var(--white-dim)',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>—</span>
                  {f}
                </li>
              ))}
            </ul>
            <Link
              to="/services/managed-it"
              className="font-body font-medium inline-flex items-center gap-2 hover:gap-3 transition-all mt-6 block"
              style={{ color: 'var(--accent)' }}
            >
              Learn More →
            </Link>
          </motion.div>

          <motion.div
            initial={false}
            animate={{ opacity: 1, x: 0 }}
            className="pillar-card relative overflow-hidden transition-colors duration-250"
            style={{
              background: 'var(--bg-3)',
              padding: '60px 52px',
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                background: 'var(--accent-glow)',
                border: '1px solid var(--accent-line)',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 28,
                fontSize: 22,
              }}
            >
              <Zap className="w-6 h-6" style={{ color: 'var(--accent)' }} />
            </div>
            <h3
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 22,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                marginBottom: 16,
                lineHeight: 1.05,
                color: 'var(--white)',
              }}
            >
              AI & <span style={{ color: 'var(--accent)' }}>Automation</span>
            </h3>
            <p
              style={{
                fontSize: 16,
                lineHeight: 1.7,
                color: 'var(--white-dim)',
                marginBottom: 36,
              }}
            >
              We identify the repetitive work draining your team's time and build automations that run themselves — powered by the latest AI tools. Every rollout includes the security guardrails to make sure AI only accesses what it should.
            </p>
            <ul className="space-y-3">
              {pillar2Features.map((f) => (
                <li
                  key={f}
                  style={{
                    fontSize: 14,
                    color: 'var(--white-dim)',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>—</span>
                  {f}
                </li>
              ))}
            </ul>
            <Link
              to="/services/ai-automation"
              className="font-body font-medium inline-flex items-center gap-2 hover:gap-3 transition-all mt-6 block"
              style={{ color: 'var(--accent)' }}
            >
              Learn More →
            </Link>
          </motion.div>
        </div>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        .pillar-card:hover { background: var(--bg-4) !important; }
        .pillar-card::before {
          content: "";
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 3px;
          background: linear-gradient(90deg, var(--accent), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .pillar-card:hover::before { opacity: 1; }
      ` }} />
    </section>
  )
}
